// @ts-nocheck
/**
 * YD Braon Scroll - 全屏滚动动画 Web Component
 * 使用 ScrollMagic 和 GSAP 实现滚动动画
 * 遵循 SOLID 原则设计
 */


/**
 * 库加载器 - 单一职责：检测和等待外部库加载
 */
class YdBraonScrollLibraryLoader {
  /**
   * 检查库是否已加载
   */
  static isLoaded() {
    const gsapAvailable = typeof gsap !== 'undefined' || typeof TweenMax !== 'undefined';
    return typeof ScrollMagic !== 'undefined' && gsapAvailable;
  }

  /**
   * 等待库加载完成
   */
  static waitForLibraries(callback) {
    const checkLibraries = () => {
      if (this.isLoaded()) {
        callback();
      } else {
        setTimeout(checkLibraries, 100);
      }
    };
    checkLibraries();
  }

  /**
   * 获取 GSAP 实例
   */
  static getGSAP() {
    if (typeof gsap !== 'undefined') {
      return gsap;
    }
    if (typeof TweenMax !== 'undefined') {
      return TweenMax;
    }
    return null;
  }
}

/**
 * 工具类 - 提供通用工具方法
 */
class YdBraonScrollUtils {
  /**
   * 将 vh 转换为像素值
   */
  static vhToPx(vh) {
    return (vh / 100) * window.innerHeight;
  }

  /**
   * 获取区块数据配置
   */
  static getBlockData(block) {
    return {
      animationType: block.dataset.animationType || 'fadeInUp',
      animationDelay: parseInt(block.dataset.animationDelay || '0'),
      animationDuration: parseInt(block.dataset.animationDuration || '800')
    };
  }
}

/**
 * 区块管理器 - 单一职责：管理区块的显示/隐藏和切换
 */
class YdBraonScrollBlockManager {
  constructor(container, blocks, scrollHeightVh, blockCount, component) {
    this.container = container;
    this.blocks = blocks;
    this.scrollHeightVh = scrollHeightVh;
    this.blockCount = blockCount;
    this.component = component;
    this.currentActiveIndex = 0;
    
    // 根据区块类型分配滚动权重
    this.blockWeights = this.calculateBlockWeights();
    this.blockOffsets = this.calculateBlockOffsets();
  }
  
  /**
   * 根据区块类型计算滚动权重
   * hero: 0.3 (无滚动动画，快速切换)
   * title: 0.9 (heading 入场动画 + subtitle 书写动画)
   * text-image: 根据段落数 (多段文字滚动)
   * video-text: 1.4 (视频+内容展示)
   */
  calculateBlockWeights() {
    const weights = [];
    this.blocks.forEach((block, index) => {
      const type = this.getBlockType(block);
      let weight = 1.0;

      switch (type) {
        case 'hero':
          weight = 0.3; // 无动画，快速切换
          break;
        case 'title':
          // 检查是否有 subtitle 的书写动画
          const hasSubtitle = block.querySelector('.sa-braon-scroll__title-subtitle p');
          weight = hasSubtitle ? 1.1 : 0.4; // 有书写动画给 0.9，否则 0.4
          break;
        case 'text-image':
          // 根据段落数量调整权重
          // 仅计算可见的段落（过滤掉 display: none 的移动端/桌面端备选文案）
          const allParagraphs = block.querySelectorAll('.sa-braon-scroll__text-image-text p');
          const visibleParagraphs = Array.from(allParagraphs).filter(p => p.offsetParent !== null);
          weight = Math.max(1.2, visibleParagraphs.length * 0.4);
          break;
        case 'video-text':
          weight = 1.4; // 视频播放后显示内容
          break;
        case 'map':
          weight = 1.8; // Map 板块需要更多停留时间
          break;
        default:
          weight = 1.0;
      }

      weights.push(weight);
    });
    return weights;
  }
  
  /**
   * 根据权重计算每个区块的起始偏移量
   */
  calculateBlockOffsets() {
    const totalWeight = this.blockWeights.reduce((sum, w) => sum + w, 0);
    const totalHeight = YdBraonScrollUtils.vhToPx(this.scrollHeightVh);
    const offsets = [0];
    
    let currentOffset = 0;
    for (let i = 0; i < this.blocks.length - 1; i++) {
      const blockHeight = (this.blockWeights[i] / totalWeight) * totalHeight;
      currentOffset += blockHeight;
      offsets.push(currentOffset);
    }
    
    return offsets;
  }
  
  /**
   * 获取区块的滚动持续时间（根据权重）
   */
  getBlockDurationByIndex(index) {
    const totalWeight = this.blockWeights.reduce((sum, w) => sum + w, 0);
    const totalHeight = YdBraonScrollUtils.vhToPx(this.scrollHeightVh);
    return (this.blockWeights[index] / totalWeight) * totalHeight;
  }
  
  /**
   * 获取区块的起始偏移量
   */
  getBlockOffset(index) {
    return this.blockOffsets[index] || 0;
  }

  /**
   * 检查区块类型
   */
  getBlockType(block) {
    if (block.classList.contains('sa-braon-scroll__text-image')) {
      return 'text-image';
    }
    if (block.classList.contains('sa-braon-scroll__video-text')) {
      return 'video-text';
    }
    if (block.classList.contains('sa-braon-scroll__hero')) {
      return 'hero';
    }
    if (block.classList.contains('sa-braon-scroll__title')) {
      return 'title';
    }
    if (block.classList.contains('sa-braon-scroll__map')) {
      return 'map';
    }
    return 'unknown';
  }
  
  /**
   * 检查区块是否有媒体内容（图片或视频）
   */
  hasMediaContent(block) {
    const hasImage = block.querySelector('img, .sa-braon-scroll__text-image-media, .sa-braon-scroll__video-text-image-layer');
    const hasVideo = block.querySelector('video, .sa-braon-scroll__video-text-video-layer');
    return !!(hasImage || hasVideo);
  }
  
  /**
   * 控制背景显示/隐藏（仅透明度）
   * 注意：现在背景始终显示，不再动态切换透明度
   * 每个有媒体内容的区块会通过 z-index 自然覆盖背景
   */
  toggleBackground(show, gsapLib) {
    if (!this.component) return;
    const background = this.component.querySelector('.sa-braon-scroll__box');
    if (!background) return;
    
    gsapLib.to(background, 0.3, {
      background: show ? 'transparent' : '#fff',
      ease: typeof gsap !== 'undefined' ? 'power2.out' : Power2.easeOut
    });
  }

  /**
   * 显示指定区块
   */
  showBlock(block, index, gsapLib) {
    this.currentActiveIndex = index;
    
    // 在动画前：根据当前区块是否有媒体内容来控制背景显示/隐藏
    const hasMedia = this.hasMediaContent(block);
    this.toggleBackground(!hasMedia, gsapLib);
    
    // 隐藏其他区块
    this.blocks.forEach((otherBlock, otherIndex) => {
      if (otherIndex !== index) {
        this.hideBlock(otherBlock, gsapLib);
      }
    });

    const blockType = this.getBlockType(block);
    const isHero = blockType === 'hero';

    // 标记区块目标状态为显示，用于 hideBlock 的 onComplete 判断
    block.dataset.targetState = 'visible';
    
    // 先停止该区块上之前的动画
    gsapLib.killTweensOf(block);
    
    // 显示当前区块
    block.classList.add('active');
    block.style.visibility = 'visible';
    gsapLib.to(block, 1.0, {
      opacity: 1,
      ease: typeof gsap !== 'undefined' ? 'power2.out' : Power2.easeOut
    });
    
    // 标记区块已显示过（用于控制入场动画只执行一次）
    block.dataset.hasShown = 'true';
  }


  /**
   * 隐藏指定区块（带动画）
   */
  hideBlock(block, gsapLib) {
    block.classList.remove('active');
    
    // 标记区块目标状态为隐藏
    block.dataset.targetState = 'hidden';
    
    // 先停止该区块上之前的动画
    gsapLib.killTweensOf(block);
    
    gsapLib.to(block, 0.8, {
      opacity: 0,
      ease: typeof gsap !== 'undefined' ? 'power2.out' : Power2.easeOut,
      onComplete: () => {
        // 只有当目标状态仍然是隐藏时才设置 visibility
        if (block.dataset.targetState === 'hidden') {
          block.style.visibility = 'hidden';
        }
        const video = block.querySelector('video');
        if (video) {
          video.pause();
        }
      }
    });
  }
  
  /**
   * 恢复 hero 区块内文字的可见状态（不执行动画）
   */
  restoreHeroText(block, gsapLib) {
    const textElements = block.querySelectorAll('.sa-braon-scroll__hero-text-content, .sa-braon-scroll__hero-watch');
    textElements.forEach(el => {
      gsapLib.set(el, {
        opacity: 1,
        visibility: 'visible',
        y: 0,
        x: 0,
        scale: 1
      });
    });
  }

  /**
   * 初始化第一个区块
   */
  initFirstBlock(gsapLib, animationManager) {
    if (this.blocks.length > 0) {
      const firstBlock = this.blocks[0];
      
      // 初始化时根据第一个区块是否有媒体来设置背景
      const hasMedia = this.hasMediaContent(firstBlock);
      this.toggleBackground(!hasMedia, gsapLib);
      
      firstBlock.classList.add('active');
      firstBlock.style.visibility = 'visible';
      firstBlock.style.opacity = '1';
      animationManager.animateTextElements(firstBlock, gsapLib, 0);
    }
  }

  /**
   * 计算区块的滚动持续时间（统一方法，向后兼容）
   */
  getBlockDuration() {
    // 返回平均值，用于不需要精确控制的场景
    return YdBraonScrollUtils.vhToPx(this.scrollHeightVh / this.blockCount);
  }

}

/**
 * 视频管理器 - 单一职责：管理视频播放和控制
 */
class YdBraonScrollVideoManager {
  constructor(animationManager, component) {
    this.animationManager = animationManager;
    this.component = component;
  }

  /**
   * 处理视频播放
   */
  handleVideoPlay(video, block) {
    if (!video) return;

    // 如果视频已经播放完毕，保持图片显示状态，不再重新播放
    if (video.dataset.videoEnded === 'true') {
      return;
    }

    // 确保视频模块的初始状态正确
    const videoTextBlock = block.closest('.sa-braon-scroll__video-text');
    if (videoTextBlock) {
      const videoLayer = videoTextBlock.querySelector('.sa-braon-scroll__video-text-video-layer');
      const imageLayer = videoTextBlock.querySelector('.sa-braon-scroll__video-text-image-layer');
      const contentArea = videoTextBlock.querySelector('.sa-braon-scroll__video-text-content');
      
      // 重置状态：显示视频层，隐藏图片层和内容区域
      if (videoLayer) {
        videoLayer.classList.remove('hide');
      }
      if (imageLayer) {
        imageLayer.classList.remove('show');
      }
      if (contentArea) {
        contentArea.dataset.contentVisibility = 'hidden';
      }
    }
    
    const handleVideoEnd = () => {
      if (video.dataset.videoEnded === 'true') return;
      video.dataset.videoEnded = 'true';
      this.handleVideoEnd(video, block);
    };
    
    video.removeEventListener('ended', handleVideoEnd);
    video.addEventListener('ended', handleVideoEnd, { once: true });
    
    if (video.readyState < 2) {
      video.addEventListener('loadeddata', () => {
        video.play().catch(() => {});
      }, { once: true });
      if (video.readyState === 0) {
        video.load();
      }
    } else {
      video.play().catch(() => {});
    }
    
    if (video.ended && video.dataset.videoEnded !== 'true') {
      setTimeout(handleVideoEnd, 100);
    }
  }

  /**
   * 处理视频结束
   */
  handleVideoEnd(video, block) {
    const videoTextBlock = video.closest('.sa-braon-scroll__video-text');
    if (!videoTextBlock) return;

    const videoLayer = videoTextBlock.querySelector('.sa-braon-scroll__video-text-video-layer');
    const imageLayer = videoTextBlock.querySelector('.sa-braon-scroll__video-text-image-layer');
    const contentArea = videoTextBlock.querySelector('.sa-braon-scroll__video-text-content');
    const gsapLib = YdBraonScrollLibraryLoader.getGSAP();
    
    if (videoLayer) videoLayer.classList.add('hide');
    if (imageLayer) imageLayer.classList.add('show');
    if (contentArea) {
      contentArea.dataset.contentVisibility = 'visible';
      if (gsapLib) {
        this.animationManager.animateVideoTextContent(contentArea, gsapLib);
      }
    }
  }

  /**
   * 处理无视频的区块
   */
  handleBlockWithoutVideo(block) {
    const videoTextBlock = block.closest('.sa-braon-scroll__video-text');
    if (!videoTextBlock) return;

    const imageLayer = videoTextBlock.querySelector('.sa-braon-scroll__video-text-image-layer');
    const contentArea = videoTextBlock.querySelector('.sa-braon-scroll__video-text-content');
    const gsapLib = YdBraonScrollLibraryLoader.getGSAP();

    if (imageLayer) imageLayer.classList.add('show');
    if (contentArea) {
      contentArea.dataset.contentVisibility = 'visible';
      setTimeout(() => {
        if (gsapLib) {
          this.animationManager.animateVideoTextContent(contentArea, gsapLib);
        }
      }, 300);
    }
  }

  /**
   * 处理 Skip 按钮点击
   */
  handleSkipButton(button) {
    const videoTextBlock = button.closest('.sa-braon-scroll__video-text');
    if (!videoTextBlock) return;

    const video = videoTextBlock.querySelector('video');
    const videoLayer = videoTextBlock.querySelector('.sa-braon-scroll__video-text-video-layer');
    const imageLayer = videoTextBlock.querySelector('.sa-braon-scroll__video-text-image-layer');
    const contentArea = videoTextBlock.querySelector('.sa-braon-scroll__video-text-content');
    const gsapLib = YdBraonScrollLibraryLoader.getGSAP();
    
    if (video) {
      video.pause();
      video.dataset.videoEnded = 'true';
    }
    
    if (videoLayer) videoLayer.classList.add('hide');
    if (imageLayer) imageLayer.classList.add('show');
    if (contentArea) {
      contentArea.dataset.contentVisibility = 'visible';
      if (gsapLib) {
        this.animationManager.animateVideoTextContent(contentArea, gsapLib);
      }
    }
  }

  /**
   * 初始化视频控制
   */
  initVideoControls() {
    if (!this.component) return;
    
    const watchButtons = this.component.querySelectorAll('.sa-braon-scroll__hero-watch');
    watchButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        // 优先从 data-video-src 获取，如果没有则从 hero 区块的视频元素获取
        let videoSrc = button.getAttribute('data-video-src');
        
        // 如果没有 data-video-src，尝试从 hero 区块的视频元素获取
        if (!videoSrc) {
          const heroBlock = button.closest('.sa-braon-scroll__hero');
          if (heroBlock) {
            // 优先使用桌面视频，如果没有则使用移动端视频
            const desktopVideo = heroBlock.querySelector('.sa-braon-scroll__hero-video--desktop');
            const mobileVideo = heroBlock.querySelector('.sa-braon-scroll__hero-video--mobile');
            const videoElement = desktopVideo || mobileVideo;
            
            if (videoElement) {
              const source = videoElement.querySelector('source');
              if (source) {
                videoSrc = source.getAttribute('src');
              } else if (videoElement.src) {
                videoSrc = videoElement.src;
              }
            }
          }
        }
        
        if (videoSrc) {
          // 在用户点击事件中直接调用，确保全屏 API 可以工作
          this.openVideoModal(videoSrc, e);
        } else {
          console.warn('No video source found for hero watch button');
        }
      });
    });

    const skipButtons = this.component.querySelectorAll('.sa-braon-scroll__video-text-skip');
    skipButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleSkipButton(button);
      });
    });
  }

  /**
   * 打开全屏视频播放（使用 HTML5 Fullscreen API）
   */
  openVideoModal(videoSrc, clickEvent) {
    // 创建视频元素
    const video = document.createElement('video');
    video.src = videoSrc;
    video.controls = true;
    video.playsInline = true;
    // 不设置 muted，保持有声音
    video.style.width = '100%';
    video.style.height = '100%';
    video.style.objectFit = 'contain';
    video.style.backgroundColor = '#000';

    // 创建全屏容器
    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.top = '0';
    container.style.left = '0';
    container.style.width = '100%';
    container.style.height = '100%';
    container.style.backgroundColor = '#000';
    container.style.display = 'flex';
    container.style.alignItems = 'center';
    container.style.justifyContent = 'center';
    container.style.zIndex = '9999';
    container.id = 'sa-braon-scroll-video-fullscreen';

    // 创建关闭按钮
    const closeBtn = document.createElement('button');
    closeBtn.className = 'sa-braon-scroll__video-modal-close';
    closeBtn.setAttribute('aria-label', 'Close video');
    closeBtn.innerHTML = `
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M18 6L6 18M6 6L18 18" stroke="#000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;
    // 样式通过 CSS 类控制，不设置内联样式以避免覆盖 hover 效果

    container.appendChild(video);
    container.appendChild(closeBtn);
    document.body.appendChild(container);

    // 进入全屏模式（必须在用户交互事件中直接调用）
    const enterFullscreen = () => {
      const requestFullscreen = container.requestFullscreen || 
                                container.webkitRequestFullscreen || 
                                container.mozRequestFullScreen || 
                                container.msRequestFullscreen;
      
      if (requestFullscreen) {
        requestFullscreen.call(container).then(() => {
          // 全屏成功后播放视频
          video.play().catch(err => {
            console.log('Video play error:', err);
          });
        }).catch(err => {
          console.log('Fullscreen error:', err);
          // 如果全屏失败，仍然显示视频并播放
          video.play().catch(() => {});
        });
      } else {
        // 如果不支持全屏，直接播放视频
        video.play().catch(() => {});
      }
    };

    // 立即尝试进入全屏（必须在用户点击事件的调用栈中）
    // 使用 requestAnimationFrame 确保在下一个事件循环中执行，但仍然在用户交互上下文中
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        enterFullscreen();
      });
    });

    // 关闭功能
    const closeVideo = () => {
      // 退出全屏
      if (document.fullscreenElement || document.webkitFullscreenElement || 
          document.mozFullScreenElement || document.msFullscreenElement) {
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        } else if (document.mozCancelFullScreen) {
          document.mozCancelFullScreen();
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen();
        }
      }

      if (video) {
        video.pause();
        video.currentTime = 0;
      }
      if (container && container.parentNode) {
        container.remove();
      }
    };

    // 关闭按钮事件
    closeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      closeVideo();
    });

    // 关闭按钮悬停效果 - 更新 SVG 颜色
    const svgPath = closeBtn.querySelector('svg path');
    if (svgPath) {
      closeBtn.addEventListener('mouseenter', () => {
        svgPath.setAttribute('stroke', '#ffffff');
      });
      closeBtn.addEventListener('mouseleave', () => {
        svgPath.setAttribute('stroke', '#000');
      });
    }

    // 点击背景关闭（如果不在全屏模式）
    container.addEventListener('click', (e) => {
      if (e.target === container && !document.fullscreenElement) {
        closeVideo();
      }
    });

    // 监听全屏变化事件
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement && !document.webkitFullscreenElement && 
          !document.mozFullScreenElement && !document.msFullscreenElement) {
        // 退出全屏时关闭视频
        closeVideo();
        document.removeEventListener('fullscreenchange', handleFullscreenChange);
        document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
        document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    // 视频播放完成后自动关闭（可选）
    video.addEventListener('ended', () => {
      setTimeout(closeVideo, 1000);
    });
  }
}

/**
 * 动画管理器 - 单一职责：管理所有动画逻辑
 */
class YdBraonScrollAnimationManager {
  constructor() {}

  /**
   * 动画文字元素
   */
  animateTextElements(block, gsapLib, blockIndex) {
    const blockData = YdBraonScrollUtils.getBlockData(block);
    const blockType = block.classList.length > 0 ? block.classList[0] : '';
    const isHeroBlock = blockType && blockType.includes('hero');
    const isTitleBlock = blockType && blockType.includes('title');
    
    // Title 区块：heading 只做入场动画，不跟随滚动
    if (isTitleBlock) {
      this.animateTitleBlock(block, gsapLib);
      return;
    }
    
    // 其他区块：处理 .wow 元素
    const allTextElements = block.querySelectorAll('.wow');
    // 过滤掉不可见的元素
    const textElements = Array.from(allTextElements).filter(el => el.offsetParent !== null);
    
    textElements.forEach((el, textIndex) => {
      const animationType = el.dataset.animationType || blockData.animationType || 'fadeInUp';
      const baseDelay = blockIndex > 0 ? 300 : 0;
      const elementDelay = textIndex * (isHeroBlock ? 200 : 150);
      const delay = parseInt(el.dataset.delay || '') || (baseDelay + elementDelay);
      const duration = parseInt(el.dataset.duration || '') || blockData.animationDuration || (isHeroBlock ? 1200 : 1000);
      
      this.setInitialAnimationState(el, animationType);
      this.executeAnimation(el, gsapLib, animationType, delay, duration);
    });
  }
  
  /**
   * Title 区块动画 - heading 只做入场动画，subtitle 书写动画由滚动控制
   */
  animateTitleBlock(block, gsapLib) {
    // Heading 入场动画
    const heading = block.querySelector('.sa-braon-scroll__title-heading');
    if (heading) {
      const animationType = heading.dataset.animationType || 'fadeInUp';
      const delay = parseInt(heading.dataset.animationDelay || '0');
      const duration = parseInt(heading.dataset.animationDuration || '800');
      
      this.setInitialAnimationState(heading, animationType);
      this.executeAnimation(heading, gsapLib, animationType, delay, duration);
      
      // Heading 内的 p 和 suffix 元素
      const headingP = heading.querySelector('p');
      const suffix = heading.querySelector('.sa-braon-scroll__title-suffix');
      
      if (headingP) {
        this.setInitialAnimationState(headingP, animationType);
        this.executeAnimation(headingP, gsapLib, animationType, delay, duration);
      }
      
      if (suffix) {
        const suffixType = suffix.dataset.animationType || 'fadeIn';
        const suffixDelay = parseInt(suffix.dataset.animationDelay || '200');
        const suffixDuration = parseInt(suffix.dataset.animationDuration || '600');
        this.setInitialAnimationState(suffix, suffixType);
        this.executeAnimation(suffix, gsapLib, suffixType, suffixDelay, suffixDuration);
      }
    }
    
    // Subtitle 容器入场动画（p 元素的书写动画由 createSubtitleBackgroundScene 滚动控制）
    const subtitle = block.querySelector('.sa-braon-scroll__title-subtitle');
    if (subtitle) {
      const subtitleType = subtitle.dataset.animationType || 'fadeInUp';
      const subtitleDelay = parseInt(subtitle.dataset.animationDelay || '400');
      const subtitleDuration = parseInt(subtitle.dataset.animationDuration || '800');
      
      this.setInitialAnimationState(subtitle, subtitleType);
      this.executeAnimation(subtitle, gsapLib, subtitleType, subtitleDelay, subtitleDuration);
      
      // 注意：subtitle 内的 p 元素不在这里处理
      // 它的 --bg-size 书写动画由 createSubtitleBackgroundScene 通过滚动控制
    }
  }

  /**
   * 动画视频文本内容
   */
  animateVideoTextContent(contentArea, gsapLib) {
    const textElements = contentArea.querySelectorAll('.wow');
    
    
    textElements.forEach((el, index) => {
      const animationType = el.dataset.animationType || 'fadeInUp';
      const delay = parseInt(el.dataset.delay || '') || (index * 200);
      const duration = parseInt(el.dataset.duration || '') || 800;
      
      this.setInitialAnimationState(el, animationType);
      const animation = this.executeAnimation(el, gsapLib, animationType, delay, duration);
    });
  }

  /**
   * 设置动画初始状态
   */
  setInitialAnimationState(el, animationType) {
    const states = {
      'fadeInUp': { opacity: '0', transform: 'translateY(30px)' },
      'slideInUp': { opacity: '0', transform: 'translateY(30px)' },
      'slideInLeft': { opacity: '0', transform: 'translateX(-100px)' },
      'slideInRight': { opacity: '0', transform: 'translateX(100px)' },
      'zoomIn': { opacity: '0', transform: 'scale(0.5)' }
    };
    
    const state = states[animationType] || { opacity: '0' };
    Object.assign(el.style, state);
  }

  /**
   * 执行动画
   */
  executeAnimation(el, gsapLib, animationType, delay, duration) {
    el.style.visibility = 'visible';
    
    const animationConfig = {
      opacity: 1,
      ease: typeof gsap !== 'undefined' ? 'power2.out' : Power2.easeOut,
      delay: delay / 1000,
      duration: duration / 1000
    };

    const transformMap = {
      'fadeInUp': { y: 0 },
      'slideInUp': { y: 0 },
      'slideInLeft': { x: 0 },
      'slideInRight': { x: 0 },
      'zoomIn': { scale: 1 }
    };

    if (transformMap[animationType]) {
      Object.assign(animationConfig, transformMap[animationType]);
    }

    if (animationType === 'background-reveal' && el.parentElement && el.parentElement.classList.contains('sa-braon-scroll__title-subtitle')) {
      Object.assign(animationConfig, { 
        '--bg-size': '100%',
        opacity: 1,
        delay: delay / 1000 + 0.3
      });
    }

    gsapLib.to(el, animationConfig);
  }
}

/**
 * 进度环管理器 - 单一职责：管理进度环动画
 */
class YdBraonScrollProgressRing {
  constructor(component, scrollHeightVh, blockCount, controller) {
    this.component = component;
    this.scrollHeightVh = scrollHeightVh;
    this.blockCount = blockCount;
    this.controller = controller;
  }

  /**
   * 初始化进度环
   */
  init() {
    const ring = this.component.querySelector('.sa-braon-scroll__hero-ring');
    if (!ring) return;

    const icon = ring.querySelector('.sa-braon-scroll__hero-ring-icon');
    
    // 进度环使用 CSS animation 自动循环播放，无需 JS 控制
    // 点击图标暂停/播放视频
    if (icon) {
      icon.addEventListener('click', () => {
        this.handleRingClick(ring);
      });
    }
  }

  /**
   * 处理进度环点击 - 暂停/播放视频
   */
  handleRingClick(ring) {
    const heroBlock = this.component.querySelector('.sa-braon-scroll__hero');
    if (!heroBlock) return;
    
    const video = heroBlock.querySelector('video');
    if (!video) return;
    
    if (video.paused) {
      video.play();
      ring.classList.remove('paused');
    } else {
      video.pause();
      ring.classList.add('paused');
    }
  }
}

/**
 * ScrollMagic 场景构建器 - 单一职责：构建 ScrollMagic 场景
 */
class YdBraonScrollSceneBuilder {
  constructor(container, controller, scrollHeightVh, blockCount) {
    this.container = container;
    this.controller = controller;
    this.scrollHeightVh = scrollHeightVh;
    this.blockCount = blockCount;
  }

  /**
   * 创建区块切换场景
   */
  createBlockSwitchScene(block, index, blockManager, videoManager, animationManager) {
    const gsapLib = YdBraonScrollLibraryLoader.getGSAP();
    if (!gsapLib) return null;

    // 使用按权重计算的持续时间和偏移量
    const sceneDuration = blockManager.getBlockDurationByIndex(index);
    const startOffset = blockManager.getBlockOffset(index);
    
    // 计算入场触发点：应该在前一屏离场之前触发，确保无缝衔接
    let sceneOffset;
    
    if (index === 0) {
      // 第一屏直接从开始触发
      sceneOffset = 0;
    } else {
      // 其他屏：在前一屏离场之前 50px 触发入场
      // 前一屏离场点 = 前一屏结束位置 - 50
      // 下一屏入场点 = 前一屏离场点 - 50 = 前一屏结束位置 - 100
      const previousIndex = index - 1;
      const previousStartOffset = blockManager.getBlockOffset(previousIndex);
      const previousDuration = blockManager.getBlockDurationByIndex(previousIndex);
      const previousEndOffset = previousStartOffset + previousDuration;
      // 在前一屏离场之前触发，确保平滑过渡
      sceneOffset = previousEndOffset - 100;
    }

    return new ScrollMagic.Scene({
      triggerElement: this.container,
      triggerHook: 0,
      duration: sceneDuration * 0.8,
      offset: sceneOffset
    })
    .on('enter', () => {
      // 检查是否是第一次显示
      const isFirstShow = block.dataset.hasShown !== 'true';
      
      blockManager.showBlock(block, index, gsapLib);
      
      const video = block.querySelector('video');
      if (video) {
        videoManager.handleVideoPlay(video, block);
      } else {
        videoManager.handleBlockWithoutVideo(block);
      }

      // 只在第一次入场时执行动画
      if (index > 0 && isFirstShow) {
        animationManager.animateTextElements(block, gsapLib, index);
      }
    })
    .addTo(this.controller);
  }

  /**
   * 创建区块离开场景
   */
  createBlockLeaveScene(block, index, blockManager) {
    const gsapLib = YdBraonScrollLibraryLoader.getGSAP();
    if (!gsapLib) return null;

    // 使用按权重计算的持续时间和偏移量
    const sceneDuration = blockManager.getBlockDurationByIndex(index);
    const startOffset = blockManager.getBlockOffset(index);
    const endOffset = startOffset + sceneDuration;

    return new ScrollMagic.Scene({
      triggerElement: this.container,
      triggerHook: 0,
      duration: sceneDuration * 0.8,
      offset: endOffset - 50
    })
    .on('enter', () => {
      if (blockManager.currentActiveIndex === index) {
        blockManager.hideBlock(block, gsapLib);
      }
    })
    .addTo(this.controller);
  }

  /**
   * 创建标题背景渐变动画场景
   */
  createSubtitleBackgroundScene(paragraph, blockIndex, blockManager) {
    const gsapLib = YdBraonScrollLibraryLoader.getGSAP();
    if (!gsapLib) return null;

    // 使用按权重计算的持续时间和偏移量
    const sceneDuration = blockManager.getBlockDurationByIndex(blockIndex);
    const startOffset = blockManager.getBlockOffset(blockIndex);

    return new ScrollMagic.Scene({
      triggerElement: this.container,
      triggerHook: 0,
      duration: sceneDuration,
      offset: startOffset,
      scrub: true
    })
    .setTween(gsapLib.to(paragraph, 1, {
      '--bg-size': '100%',
      ease: typeof gsap !== 'undefined' ? 'none' : Power0.easeNone
    }))
    .addTo(this.controller);
  }

  /**
   * 创建文字滚动场景
   */
  createTextScrollScene(textElement, blockIndex, paragraphs, blockManager) {
    const gsapLib = YdBraonScrollLibraryLoader.getGSAP();
    if (!gsapLib) return null;

    // 使用按权重计算的持续时间和偏移量
    const sceneDuration = blockManager.getBlockDurationByIndex(blockIndex);
    const startOffset = blockManager.getBlockOffset(blockIndex);

    // 计算整个文字区域的高度
    const textElementHeight = textElement.offsetHeight;
    const viewportCenter = window.innerHeight / 2;
    // 让整个文字区域的中心位于视口中心，然后往上移动50px
    const initialOffset = viewportCenter - (textElementHeight / 2) - 50;
    const finalOffset = initialOffset - (window.innerHeight * 0.3);
    const moveDistance = finalOffset - initialOffset;

    // 初始位置：让整个文字区域的中心在屏幕中间
    textElement.style.transform = `translateY(${initialOffset}px)`;

    const scene = new ScrollMagic.Scene({
      triggerElement: this.container,
      triggerHook: 0,
      duration: sceneDuration,
      offset: startOffset,
      scrub: true
    })
    .setTween(gsapLib.to(textElement, 1, {
      y: moveDistance,
      ease: typeof gsap !== 'undefined' ? 'none' : Power0.easeNone
    }))
    .addTo(this.controller);

    return { scene, initialOffset, moveDistance };
  }

  /**
   * 创建段落高亮场景
   */
  createParagraphHighlightScene(paragraph, index, paragraphDuration, paragraphStartOffset) {
    return new ScrollMagic.Scene({
      triggerElement: this.container,
      triggerHook: 0.5,
      duration: paragraphDuration * 0.6,
      offset: paragraphStartOffset + (paragraphDuration * 0.2)
    })
    .on('enter', () => {
      paragraph.classList.add('highlight');
    })
    .on('leave', () => {
      paragraph.classList.remove('highlight');
    })
    .addTo(this.controller);
  }
}

/**
 * 主组件类 - 负责协调各个管理器
 */
class YdBraonScroll extends HTMLElement {
  constructor() {
    super();
    this.controller = null;
    this.container = null;
    this.box = null;
    this.blocks = [];
    this._scrollHeightVh = 350;
    this.blockCount = 0;
    
    // 管理器实例
    this.blockManager = null;
    this.animationManager = new YdBraonScrollAnimationManager();
    this.videoManager = null; // 将在 init() 中初始化，需要组件引用
    this.progressRing = null;
    this.sceneBuilder = null;
  }

  connectedCallback() {
    this.initialize();
  }

  disconnectedCallback() {
    this.destroy();
  }

  /**
   * 初始化组件
   */
  initialize() {
    this.box = this.querySelector('.sa-braon-scroll__box');
    if (!this.box) return;

    this.blocks = this.extractBlocks();
    this._scrollHeightVh = parseInt(this.dataset.scrollHeight || '350') || 350;
    this.blockCount = this.blocks.length;
    
    
    if (YdBraonScrollLibraryLoader.isLoaded()) {
      this.init();
    } else {
      YdBraonScrollLibraryLoader.waitForLibraries(() => this.init());
    }
  }

  /**
   * 提取区块元素
   */
  extractBlocks() {
    return Array.from(this.box.children).filter(child => 
      child.classList.contains('sa-braon-scroll__hero') ||
      child.classList.contains('sa-braon-scroll__title') ||
      child.classList.contains('sa-braon-scroll__text-image') ||
      child.classList.contains('sa-braon-scroll__video-text') ||
      child.classList.contains('sa-braon-scroll__map')
    ).map(child => /** @type {HTMLElement} */ (child));
  }

  /**
   * 初始化 ScrollMagic 和所有功能
   */
  init() {
    this.container = this.querySelector('.sa-braon-scroll__container');
    if (!this.container) {
      console.error('sa-braon-scroll: container not found');
      return;
    }
    
    
    this.controller = new ScrollMagic.Controller();
    
    // 初始化管理器
    this.blockManager = new YdBraonScrollBlockManager(
      this.container,
      this.blocks,
      this._scrollHeightVh,
      this.blockCount,
      this
    );
    
    this.sceneBuilder = new YdBraonScrollSceneBuilder(
      this.container,
      this.controller,
      this._scrollHeightVh,
      this.blockCount
    );
    
    this.progressRing = new YdBraonScrollProgressRing(
      this,
      this._scrollHeightVh,
      this.blockCount,
      this.controller
    );
    
    // 初始化视频管理器（需要组件引用）
    this.videoManager = new YdBraonScrollVideoManager(this.animationManager, this);

    const gsapLib = YdBraonScrollLibraryLoader.getGSAP();
    if (gsapLib) {
      this.blockManager.initFirstBlock(gsapLib, this.animationManager);
    }

    this.initBlockSwitching();
    this.initProgressRing();
    this.initVideoControls();
    this.initSubtitleBackgroundReveal();
    this.initTextImageScrollTransition();

    if (this.controller) {
      requestAnimationFrame(() => {
        this.controller.update();
      });
    }
  }

  /**
   * 初始化区块切换动画
   */
  initBlockSwitching() {
    const gsapLib = YdBraonScrollLibraryLoader.getGSAP();
    if (!gsapLib) return;

    this.blocks.forEach((block, index) => {
      this.sceneBuilder.createBlockSwitchScene(
        block,
        index,
        this.blockManager,
        this.videoManager,
        this.animationManager
      );

      if (index > 0 && index < this.blockCount - 1) {
        this.sceneBuilder.createBlockLeaveScene(
          block,
          index,
          this.blockManager
        );
      }
    });
  }

  /**
   * 初始化标题背景渐变动画
   */
  initSubtitleBackgroundReveal() {
    const subtitleParagraphs = this.querySelectorAll('.sa-braon-scroll__title-subtitle p');
    if (!subtitleParagraphs.length) return;

    subtitleParagraphs.forEach((p) => {
      const titleBlock = p.closest('.sa-braon-scroll__title');
      if (!titleBlock) return;

      const blockIndex = this.blocks.indexOf(/** @type {HTMLElement} */ (titleBlock));
      if (blockIndex === -1) return;

      this.sceneBuilder.createSubtitleBackgroundScene(p, blockIndex, this.blockManager);
    });
  }

  /**
   * 初始化文字滚动过渡动画
   */
  initTextImageScrollTransition() {
    const gsapLib = YdBraonScrollLibraryLoader.getGSAP();
    if (!gsapLib) return;

    const textImageBlocks = this.querySelectorAll('.sa-braon-scroll__text-image');
    if (!textImageBlocks.length) return;

    textImageBlocks.forEach((block) => {
      const blockIndex = this.blocks.indexOf(block);
      if (blockIndex === -1) return;

      const textElement = block.querySelector('.sa-braon-scroll__text-image-text');
      if (!textElement) return;

      const allParagraphs = textElement.querySelectorAll('p');
      // 仅处理可见段落
      const paragraphs = Array.from(allParagraphs).filter(p => p.offsetParent !== null);
      if (!paragraphs.length) return;

      // 等待布局完成后再设置初始位置，确保文字区域高度计算准确
      requestAnimationFrame(() => {
        const { scene, initialOffset, moveDistance } = this.sceneBuilder.createTextScrollScene(
          textElement,
          blockIndex,
          paragraphs,
          this.blockManager
        );

        if (scene) {
          // 在滚动过程中持续更新高亮，确保屏幕中间的段落高亮
          scene.on('progress', () => {
            this.updateCenterParagraphHighlight(textElement, paragraphs);
          });
          
          // 在场景开始时重新计算并设置初始位置，确保整个文字区域居中并往上50px
          scene.on('start', () => {
            const viewportCenter = window.innerHeight / 2;
            const textElementHeight = textElement.offsetHeight;
            const centerOffset = viewportCenter - (textElementHeight / 2) - 50;
            // 重新计算并设置初始位置，确保整个文字区域中心在屏幕中间并往上100px
            const currentTransform = textElement.style.transform;
            if (!currentTransform || currentTransform === 'none') {
              textElement.style.transform = `translateY(${centerOffset}px)`;
            }
            // 初始时也更新一次高亮
            this.updateCenterParagraphHighlight(textElement, paragraphs);
          });
        }

        this.initParagraphHighlight(textElement, paragraphs, blockIndex);
      });
    });
  }

  /**
   * 更新中心段落高亮
   */
  updateCenterParagraphHighlight(textElement, paragraphs) {
    const viewportCenter = window.innerHeight / 2;
    // 更严格的阈值，确保只有真正在屏幕中间的段落才高亮
    const highlightThreshold = 60; // 固定60px阈值，更精确

    let closestParagraph = null;
    let minDistance = Infinity;

    paragraphs.forEach((paragraph) => {
      const paragraphRect = paragraph.getBoundingClientRect();
      const paragraphCenter = paragraphRect.top + (paragraphRect.height / 2);
      const distanceToCenter = Math.abs(paragraphCenter - viewportCenter);
      
      // 确保段落可见且在视口内
      if (paragraphRect.bottom > 0 && paragraphRect.top < window.innerHeight) {
        if (distanceToCenter < minDistance) {
          minDistance = distanceToCenter;
          closestParagraph = paragraph;
        }
      }
    });

    // 只有最接近中心且距离小于阈值的段落才高亮
    // 如果当前已经有高亮的段落，且它仍然在合理范围内，保持高亮（避免频繁切换）
    const currentlyHighlighted = Array.from(paragraphs).find(p => p.classList.contains('highlight'));
    let shouldHighlight = false;
    let targetParagraph = null;

    if (closestParagraph && minDistance < highlightThreshold) {
      // 如果当前高亮的段落仍然接近中心，保持高亮
      if (currentlyHighlighted) {
        const currentRect = currentlyHighlighted.getBoundingClientRect();
        const currentCenter = currentRect.top + (currentRect.height / 2);
        const currentDistance = Math.abs(currentCenter - viewportCenter);
        
        // 如果当前高亮的段落仍然在阈值内，保持它
        if (currentDistance < highlightThreshold && currentRect.bottom > 0 && currentRect.top < window.innerHeight) {
          shouldHighlight = true;
          targetParagraph = currentlyHighlighted;
        } else {
          // 否则切换到新的最接近中心的段落
          shouldHighlight = true;
          targetParagraph = closestParagraph;
        }
      } else {
        // 没有当前高亮的段落，高亮最接近中心的
        shouldHighlight = true;
        targetParagraph = closestParagraph;
      }
    } else if (currentlyHighlighted) {
      // 即使没有段落严格在阈值内，如果当前高亮的段落仍然可见，保持高亮
      const currentRect = currentlyHighlighted.getBoundingClientRect();
      if (currentRect.bottom > 0 && currentRect.top < window.innerHeight) {
        const currentCenter = currentRect.top + (currentRect.height / 2);
        const currentDistance = Math.abs(currentCenter - viewportCenter);
        // 如果当前高亮的段落仍然在合理范围内（放宽到100px），保持高亮
        if (currentDistance < 100) {
          shouldHighlight = true;
          targetParagraph = currentlyHighlighted;
        }
      }
    }
    
    paragraphs.forEach((paragraph) => {
      if (shouldHighlight && paragraph === targetParagraph) {
        if (!paragraph.classList.contains('highlight')) {
          paragraph.classList.add('highlight');
        }
      } else {
        paragraph.classList.remove('highlight');
      }
    });
  }

  /**
   * 初始化段落高亮
   */
  initParagraphHighlight(textElement, paragraphs, blockIndex) {
    // 使用按权重计算的持续时间和偏移量
    const sceneDuration = this.blockManager.getBlockDurationByIndex(blockIndex);
    const startOffset = this.blockManager.getBlockOffset(blockIndex);

    paragraphs.forEach((paragraph, index) => {
      const paragraphDuration = sceneDuration / paragraphs.length;
      const paragraphStartOffset = startOffset + (paragraphDuration * index);

      this.sceneBuilder.createParagraphHighlightScene(
        paragraph,
        index,
        paragraphDuration,
        paragraphStartOffset
      );
    });

    if (paragraphs.length > 0) {
      paragraphs[0].classList.add('highlight');
    }
  }

  /**
   * 初始化进度环
   */
  initProgressRing() {
    if (this.progressRing) {
      this.progressRing.init();
    }
  }

  /**
   * 初始化视频控制
   */
  initVideoControls() {
    this.videoManager.initVideoControls();
  }

  /**
   * 滚动到指定区块
   */
  scrollToBlock(index) {
    const rect = this.getBoundingClientRect();
    const containerTop = rect.top + window.pageYOffset;
    const blockProgress = index / this.blockCount;
    const viewportHeight = window.innerHeight;
    const containerHeight = (this._scrollHeightVh / 100) * viewportHeight;
    const targetScroll = containerTop + (containerHeight * blockProgress);
    
    window.scrollTo({
      top: targetScroll,
      behavior: 'smooth'
    });
  }

  /**
   * 销毁实例
   */
  destroy() {
    // 暂停所有视频
    if (this.blocks) {
      this.blocks.forEach(block => {
        const video = block.querySelector('video');
        if (video) {
          video.pause();
          video.removeAttribute('src');
          video.load();
        }
      });
    }
    
    // 销毁 ScrollMagic 控制器
    if (this.controller) {
      this.controller.destroy(true);
      this.controller = null;
    }
    
    
    // 清理引用
    this.blockManager = null;
    this.animationManager = null;
    this.videoManager = null;
    this.progressRing = null;
    this.sceneBuilder = null;
    this.container = null;
    this.box = null;
    this.blocks = [];
  }
}

// 注册 Web Component
if (!customElements.get('sa-braon-scroll')) {
  customElements.define('sa-braon-scroll', YdBraonScroll);
}
